import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
const BASE_Z_INDEX = 200;
const OverlayZIndexContext = createContext(null);
/**
 * Provides an auto-incrementing z-index counter shared across all
 * overlays (modals and drawers). Wrap the app once near the root.
 */
export const OverlayZIndexProvider = ({ children }) => {
    const counterRef = useRef(BASE_Z_INDEX);
    const activeRef = useRef(new Set());
    const claim = useCallback(() => {
        const next = ++counterRef.current;
        activeRef.current.add(next);
        return next;
    }, []);
    const release = useCallback((z) => {
        activeRef.current.delete(z);
        // Reset counter when all overlays are closed so values stay small
        if (activeRef.current.size === 0) {
            counterRef.current = BASE_Z_INDEX;
        }
    }, []);
    return (_jsx(OverlayZIndexContext.Provider, { value: { claim, release }, children: children }));
};
/**
 * Returns a z-index that is higher than any previously opened overlay.
 * The z-index is claimed when `opened` becomes true and released when
 * it becomes false, so the newest overlay always renders on top.
 */
export const useOverlayZIndex = (opened) => {
    const ctx = useContext(OverlayZIndexContext);
    const [zIndex, setZIndex] = useState(undefined);
    const zRef = useRef(undefined);
    useEffect(() => {
        if (!ctx)
            return;
        if (opened && zRef.current === undefined) {
            const z = ctx.claim();
            zRef.current = z;
            setZIndex(z);
        }
        else if (!opened && zRef.current !== undefined) {
            ctx.release(zRef.current);
            zRef.current = undefined;
            setZIndex(undefined);
        }
    }, [opened, ctx]);
    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (ctx && zRef.current !== undefined) {
                ctx.release(zRef.current);
            }
        };
    }, [ctx]);
    return zIndex;
};
